import { useState, useEffect } from "react";
import { TerminalPane } from "../TerminalPane";
import type { MaxMode } from "./types";
import type { ResolvedRef } from "../../utils/fileRefResolve";

interface Props {
  maxMode: MaxMode;
  workbenchId: string;
  // Whether the terminal overlay surface is currently shown. The terminal is
  // a full-surface overlay (.terminal-pane is position:absolute; inset:0 over
  // .chat-workspace), so when it is visible it covers the chat/editor panes.
  // Selecting a chat / file / session flips this false so those surfaces are
  // reachable; the ActivityBar terminal button toggles it back on. Defaults
  // to true for backward compatibility with any caller that omits it.
  paneVisible?: boolean;
  // Hide the terminal overlay (the pane's × button). Distinct from onMinimize,
  // which only un-maximizes. Defaults to a no-op if a caller omits it.
  onClose?: () => void;
  onMinimize: () => void;
  onMaximize?: () => void;
  activeSessionId?: string | null;
  projectId?: string | null;
  rootPath?: string | null;
  defaultEnforce?: boolean;
  onOpenFileRef?: (ref: ResolvedRef) => void;
}

export function TerminalWorkspace({
  maxMode,
  paneVisible = true,
  onClose,
  onMinimize,
  onMaximize,
  activeSessionId = null,
  projectId,
  rootPath,
  defaultEnforce,
  onOpenFileRef,
}: Props) {
  // Show the terminal overlay only when the user is actually viewing terminals
  // (paneVisible) and chat is not maxed. We keep the pane MOUNTED while hidden
  // (display:none) so PTY connections survive the toggle.
  const visible = paneVisible && maxMode !== "chat";
  const maximized = maxMode === "terminal";

  const [enlargedTileId, setEnlargedTileId] = useState<string | null>(null);

  // Enlarge now works in BOTH the normal grid and maximized mode, so it must
  // NOT reset on un-maximize. Only clear it when the whole terminal surface is
  // hidden, so re-opening terminals starts from the normal multi-tile grid.
  useEffect(() => {
    if (!visible) setEnlargedTileId(null);
  }, [visible]);

  const handleToggleMaximized = () => {
    if (maximized) {
      onMinimize();
    } else if (onMaximize) {
      onMaximize();
    }
  };

  return (
    <div
      className={`terminal-workspace${maximized ? " terminal-workspace--max" : ""}`}
      style={visible ? undefined : { display: "none" }}
    >
      <TerminalPane
        activeSessionId={activeSessionId}
        visible={visible}
        maximized={maximized}
        onClose={onClose ?? (() => {})}
        onToggleMaximized={handleToggleMaximized}
        projectId={projectId}
        rootPath={rootPath}
        defaultEnforce={defaultEnforce}
        enlargedTileId={enlargedTileId}
        onEnlargeTile={setEnlargedTileId}
        onOpenFileRef={onOpenFileRef}
      />
    </div>
  );
}
